import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from '@/lib/auth';
import PageTitle from '@/components/PageTitle';
import Landing from '@/pages/Landing';
import PlanSalle from '@/pages/PlanSalle';
import RefSystem from '@/pages/RefSystem';
import RefMobile from '@/pages/RefMobile';
import RefAdmin from '@/pages/RefAdmin';
import ScanQR from '@/pages/ScanQR';
import Billetterie from '@/pages/Billetterie';
import SpeedInscription from '@/pages/SpeedInscription';
import Billet from '@/pages/Billet';
import Login from '@/pages/Login';
import Sso from '@/pages/Sso';
import Register from '@/pages/Register';
import VerifyEmail from '@/pages/VerifyEmail';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import StripeSuccess from '@/pages/StripeSuccess';
import SuperAdminDashboard from '@/pages/SuperAdminDashboard';
import AssoLayout from '@/pages/AssoLayout';
import AssoDashboard from '@/pages/AssoDashboard';
import AssoMembres from '@/pages/AssoMembres';
import AssoGroupes from '@/pages/AssoGroupes';
import AssoBenevoles from '@/pages/AssoBenevoles';
import AssoEvenements from '@/pages/AssoEvenements';
import AssoSpeedBusiness from '@/pages/AssoSpeedBusiness';
import AssoKPI from '@/pages/AssoKPI';
import AssoVitrineConfig from '@/pages/AssoVitrineConfig';
import PageVitrineAsso from '@/pages/PageVitrineAsso';
import AssoBureau from '@/pages/AssoBureau';
import DemandesDepart from '@/pages/DemandesDepart';
import AssoTresorerie from '@/pages/AssoTresorerie';
import AssoAnnuaire from '@/pages/AssoAnnuaire';
import AssoMessages from '@/pages/AssoMessages';
import AssoGazette from '@/pages/AssoGazette';
import AssoSponsors from '@/pages/AssoSponsors';
import AssoParametres from '@/pages/AssoParametres';
import AssoIntegrations from '@/pages/AssoIntegrations';
import AssoFormules from '@/pages/AssoFormules';
import InvitationAccept from '@/pages/InvitationAccept';
import MemberMonReseau from '@/pages/MemberMonReseau';
import RefPublic from '@/pages/RefPublic';
import CreerMonAssociation from '@/pages/CreerMonAssociation';
import MemberLayout from '@/pages/MemberLayout';
import MemberHome from '@/pages/MemberHome';
import MemberEvents from '@/pages/MemberEvents';
import MemberBillets from '@/pages/MemberBillets';
import MemberProfile from '@/pages/MemberProfile';
import MemberAdhesion from '@/pages/MemberAdhesion';
import MemberCarte from '@/pages/MemberCarte';
import RejoindreAsso from '@/pages/RejoindreAsso';
import AssoAdhesions from '@/pages/AssoAdhesions';
import AssoDons from '@/pages/AssoDons';
import MemberRecus from '@/pages/MemberRecus';
import AgendaEntreprises from '@/pages/AgendaEntreprises';
import AssoRelances from '@/pages/AssoRelances';
import CookieBanner from '@/components/CookieBanner';
import OnboardingTour from '@/components/OnboardingTour';
import MentionsLegales from '@/pages/legal/MentionsLegales';
import CGU from '@/pages/legal/CGU';
import Confidentialite from '@/pages/legal/Confidentialite';
import EducateurDashboard from '@/pages/roles/EducateurDashboard';
import ParentDashboard from '@/pages/roles/ParentDashboard';
import BenevoleDashboard from '@/pages/roles/BenevoleDashboard';
import { roleHome } from '@/lib/roleHome';

function Protected({ children, allow }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (allow && !allow.includes(user.role)) {
    return <Navigate to={roleHome(user.role)} replace />;
  }
  return children;
}

function RoleRouter() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={roleHome(user.role)} replace />;
}

export default function App() {
  const [customSlug, setCustomSlug] = useState(undefined); // undefined = chargement, null = pas de match, string = slug détecté
  const isStandardHost = (h) => /preview\.emergentagent\.com|localhost|emergent\.host/i.test(h);

  // Détection custom domain au boot
  useEffect(() => {
    const host = window.location.hostname.toLowerCase();
    if (isStandardHost(host)) { setCustomSlug(null); return; }
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/asso-by-host?host=${encodeURIComponent(host)}`)
      .then(({ data }) => setCustomSlug(data.asso?.slug || null))
      .catch(() => setCustomSlug(null));
  }, []);

  if (customSlug === undefined) {
    return <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}><div style={{ fontSize: 12, color: '#94A3B8' }}>Chargement…</div></div>;
  }

  return (
    <AuthProvider>
      <BrowserRouter>
        <PageTitle />
        <Toaster position="top-right" richColors closeButton />
        {customSlug ? (
          <Routes>
            {/* Custom domain : on affiche directement la vitrine de l'asso pour la racine */}
            <Route path="/" element={<PageVitrineAsso slugOverride={customSlug} />} />
            <Route path="/billetterie" element={<Billetterie />} />
            <Route path="/billet/:qr_code" element={<Billet />} />
            <Route path="/speed/:token" element={<SpeedInscription />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="/verify/:token" element={<VerifyEmail />} />
            <Route path="/success" element={<StripeSuccess />} />
            <Route path="/rejoindre/:code" element={<RejoindreAsso />} />
            <Route path="/asso/:slug" element={<PageVitrineAsso />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        ) : (
        <Routes>
          {/* Landing commerciale AssoPerf sur / */}
          <Route path="/" element={<Landing />} />

          {/* Mockups visuels */}
          <Route path="/plan-de-salle" element={<PlanSalle />} />
          <Route path="/ref" element={<RefSystem />} />
          <Route path="/ref-mobile" element={<RefMobile />} />
          <Route path="/ref-admin" element={<RefAdmin />} />

          {/* Modules LIVE */}
          <Route path="/admin-asso" element={<Protected allow={['president','vice_president','secretaire','vice_secretaire','tresorier']}><RefAdmin live /></Protected>} />
          <Route path="/scan" element={<Protected allow={['president','vice_president','secretaire','vice_secretaire','tresorier','membre']}><ScanQR /></Protected>} />
          <Route path="/billetterie" element={<Billetterie />} />
          <Route path="/billet/:qr_code" element={<Billet />} />
          <Route path="/speed/:token" element={<SpeedInscription />} />

          {/* Auth */}
          <Route path="/login" element={<Login />} />
          <Route path="/sso" element={<Sso />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/verify/:token" element={<VerifyEmail />} />
          <Route path="/success" element={<StripeSuccess />} />
          <Route path="/app" element={<RoleRouter />} />

          {/* Page publique REF Figeac (legacy + nouvelle vitrine Phase G) */}
          <Route path="/ref-figeac" element={<RefPublic />} />
          <Route path="/asso/:slug" element={<PageVitrineAsso />} />

          {/* Acceptation d'invitation (public) */}
          <Route path="/invitation/:token" element={<InvitationAccept />} />

          {/* Rejoindre une asso via son code (public, self-service) */}
          <Route path="/rejoindre/:code" element={<RejoindreAsso />} />

          {/* Guide officiel : comment créer son asso (renvoie aux services publics) */}
          <Route path="/creer-mon-association" element={<CreerMonAssociation />} />

          {/* Pages légales */}
          <Route path="/mentions-legales" element={<MentionsLegales />} />
          <Route path="/cgu" element={<CGU />} />
          <Route path="/confidentialite" element={<Confidentialite />} />

          {/* Super admin (Guillaume) */}
          <Route path="/admin" element={<Protected allow={['super_admin']}><SuperAdminDashboard /></Protected>} />

          {/* Asso staff */}
          <Route path="/dashboard" element={<Protected allow={['president','vice_president','secretaire','vice_secretaire','tresorier']}><AssoLayout /></Protected>}>
            <Route index element={<AssoDashboard />} />
            <Route path="membres" element={<AssoMembres />} />
            <Route path="groupes" element={<AssoGroupes />} />
            <Route path="benevoles" element={<AssoBenevoles />} />
            <Route path="adhesions" element={<AssoAdhesions />} />
            <Route path="formules" element={<AssoFormules />} />
            <Route path="bureau" element={<AssoBureau />} />
            <Route path="demandes-depart" element={<DemandesDepart />} />
            <Route path="tresorerie" element={<AssoTresorerie />} />
            <Route path="dons" element={<AssoDons />} />
            <Route path="annuaire" element={<AssoAnnuaire />} />
            <Route path="messages" element={<AssoMessages />} />
            <Route path="gazette" element={<AssoGazette />} />
            <Route path="agenda-pro" element={<AgendaEntreprises />} />
            <Route path="relances" element={<AssoRelances />} />
            <Route path="evenements" element={<AssoEvenements />} />
            <Route path="speed-business" element={<AssoSpeedBusiness />} />
            <Route path="speed-business/:sessionId" element={<AssoSpeedBusiness />} />
            <Route path="kpi" element={<AssoKPI />} />
            <Route path="config/vitrine" element={<AssoVitrineConfig />} />
            <Route path="sponsors" element={<AssoSponsors />} />
            <Route path="integrations" element={<AssoIntegrations />} />
            <Route path="parametres" element={<AssoParametres />} />
          </Route>

          {/* Member space (white-label) */}
          <Route path="/espace" element={<Protected allow={['membre','joueur','adherent','president','vice_president','secretaire','vice_secretaire','tresorier']}><MemberLayout /></Protected>}>
            <Route index element={<MemberHome />} />
            <Route path="evenements" element={<MemberEvents />} />
            <Route path="annuaire" element={<AssoAnnuaire />} />
            <Route path="messages" element={<AssoMessages />} />
            <Route path="gazette" element={<AssoGazette />} />
            <Route path="agenda-pro" element={<AgendaEntreprises />} />
            <Route path="billets" element={<MemberBillets />} />
            <Route path="adhesion" element={<MemberAdhesion />} />
            <Route path="carte" element={<MemberCarte />} />
            <Route path="recus" element={<MemberRecus />} />
            <Route path="mon-reseau" element={<MemberMonReseau />} />
            <Route path="profil" element={<MemberProfile />} />
          </Route>

          {/* Interfaces dédiées par rôle */}
          <Route path="/educateur" element={<Protected allow={['educateur']}><EducateurDashboard /></Protected>} />
          <Route path="/parent" element={<Protected allow={['parent']}><ParentDashboard /></Protected>} />
          <Route path="/benevole" element={<Protected allow={['benevole']}><BenevoleDashboard /></Protected>} />

          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
        )}
        <CookieBanner />
        <OnboardingTour />
      </BrowserRouter>
    </AuthProvider>
  );
}
